import { useEffect, useRef } from "react";
import { useQueryClient, type QueryFilters } from "@tanstack/react-query";
import { createResyncChannel, type ResyncChannelBinding } from "@/lib/realtime/channel";
import { createCoalescer } from "@/lib/realtime/coalesce";

/** Long enough to absorb a bulk upload's per-row events, short enough to feel live. */
const DEFAULT_WINDOW_MS = 500;

export interface UseRealtimeInvalidateOptions {
  /** Skip the subscription entirely — e.g. before the org context is ready. */
  enabled?: boolean;
  /** Must be unique per (scope, consumer). Build it with `realtimeTopic`. */
  topic: string;
  bindings: ResyncChannelBinding[];
  /**
   * Query caches to flush when something changes.
   *
   * Filters rather than bare keys, so a caller can scope with `predicate`.
   * A bare prefix like `["building-doc-stats"]` would flush every building in
   * the cache; pass `{ queryKey: [...], predicate: q => q.queryKey[2] === id }`.
   */
  invalidate?: QueryFilters[];
  /** For MST stores and anything else that is not a react-query cache. */
  onChange?: () => void;
  /**
   * The channel re-joined, so events were lost while it was down. Runs before
   * the flush it triggers, so a caller scoping its invalidation by what the
   * payloads named can widen it back out for this one.
   */
  onResync?: () => void;
  /**
   * Also flush on the FIRST join, not just on re-joins.
   *
   * For caches whose only path back to the database is this subscription
   * (`staleTime: Infinity`), where the gap in front of a join is every moment
   * the consumer was unmounted — and for a subscription whose filter changes,
   * since that tears the channel down and the replacement's first SUBSCRIBED
   * would otherwise read as an initial join with nothing covering the swap.
   *
   * The initial join carries the same unknown gap a re-join does, so it runs
   * `onResync` too: a caller that widens its scope there has to widen it here.
   * Costs one refetch per join on top of the consumer's own initial fetch.
   */
  flushOnJoin?: boolean;
  /** Coalescing window. Defaults to 500ms. */
  windowMs?: number;
}

/**
 * Bridge a `postgres_changes` channel to react-query caches (and/or an MST
 * store), coalescing bursts and recovering from dropped sockets.
 *
 * Both the per-event path and the re-join `onResync` run through one shared
 * coalescer, so a re-join landing in the middle of a burst cannot double-fire.
 *
 * Invalidation deliberately leaves `refetchType` at its "active" default:
 * refetching inactive queries would wake, for instance, all seven
 * `building-doc-stats` entries in a backgrounded tab.
 */
export function useRealtimeInvalidate(opts: UseRealtimeInvalidateOptions): void {
  const {
    enabled = true,
    topic,
    bindings,
    invalidate,
    onChange,
    onResync,
    flushOnJoin = false,
    windowMs = DEFAULT_WINDOW_MS,
  } = opts;

  const queryClient = useQueryClient();

  // Read through refs so callers can pass inline array and arrow literals
  // without churning the channel on every render. Filled in an effect rather
  // than during render (a render that never commits must not be what the
  // socket callbacks see) and declared before the subscription effect, so the
  // refs are current by the time that one reads them.
  const invalidateRef = useRef(invalidate);
  const onChangeRef = useRef(onChange);
  const onResyncRef = useRef(onResync);
  const bindingsRef = useRef(bindings);

  useEffect(() => {
    invalidateRef.current = invalidate;
    onChangeRef.current = onChange;
    onResyncRef.current = onResync;
    bindingsRef.current = bindings;
  });

  // A string, so an inline `bindings` literal is free: the effect re-runs only
  // when the subscription's actual shape changes.
  const bindingSignature = bindings
    .map((b) => `${b.schema ?? "public"}.${b.table}.${b.event}.${b.filter ?? ""}`)
    .join("|");

  useEffect(() => {
    if (!enabled) return;

    // Runs from the socket dispatch on the leading edge and from a timer on the
    // trailing one, so an exception escaping it is unhandled either way. Each
    // half is guarded separately: one failing consumer must not take the others
    // down with it, and `onChange` still has to run when an invalidation throws.
    const coalescer = createCoalescer(() => {
      // Per filter, not per loop: one caller's throwing `predicate` must not
      // swallow every invalidation queued behind it.
      for (const filters of invalidateRef.current ?? []) {
        try {
          // `.catch` as well as the try/catch: `invalidateQueries` throws
          // synchronously on a bad filter, but its refetch settles later, so a
          // rejection there would miss this block and go unhandled.
          queryClient.invalidateQueries(filters).catch((e) => {
            console.error("[realtime] invalidation rejected", topic, e);
          });
        } catch (e) {
          console.error("[realtime] invalidation failed", topic, e);
        }
      }
      try {
        onChangeRef.current?.();
      } catch (e) {
        console.error("[realtime] onChange threw", topic, e);
      }
    }, windowMs);

    // Per channel instance rather than per hook: this effect re-runs whenever
    // the subscription's shape changes, and each replacement is a fresh join
    // with a teardown gap in front of it.
    let joined = false;

    // Guarded like the binding handlers, so a throw there still leaves the
    // flush to run.
    const resync = () => {
      try {
        onResyncRef.current?.();
      } catch (e) {
        console.error("[realtime] onResync threw", topic, e);
      }
      coalescer.call();
    };

    const handle = createResyncChannel({
      topic,
      // The initial join is the channel's business only when the caller asked
      // for it; every later SUBSCRIBED reaches `onResync` below on its own.
      onStatus: (status) => {
        if (!flushOnJoin || status !== "SUBSCRIBED" || joined) return;
        joined = true;
        resync();
      },
      bindings: bindingsRef.current.map((binding, index) => ({
        ...binding,
        handler: (payload) => {
          // Through the ref, like `invalidate` and `onChange`: an inline handler
          // closing over props would otherwise be frozen at the render that
          // opened the channel. Index alignment holds because the effect
          // re-runs whenever the bindings' shape (and so their order) changes.
          //
          // Guarded so a consumer that throws still gets its invalidation: the
          // handler is an extra, and an exception here would otherwise both
          // skip the refetch and escape into the socket callback.
          let result: void | Promise<void>;
          try {
            result = bindingsRef.current[index]?.handler?.(payload);
          } catch (e) {
            console.error("[realtime] binding handler threw", topic, e);
          }
          coalescer.call();
          // Returned, not dropped: an async handler's rejection is only logged
          // if the promise reaches the shared channel's guard.
          return result;
        },
      })),
      // A re-join means events were lost while the socket was down, so the
      // caches are suspect regardless of what did or did not arrive. The
      // caller hears about it first.
      onResync: resync,
    });

    return () => {
      coalescer.cancel();
      handle.unsubscribe();
    };
    // bindingSignature stands in for `bindings`; the rest is read through refs.
  }, [enabled, topic, bindingSignature, flushOnJoin, windowMs, queryClient]);
}
